import {
  css, type Interpolation,
} from '@emotion/react';
import styled from '@emotion/styled';
import {
  type ChangeEventHandler, forwardRef, type ReactNode,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import { TriStateRange } from '../../constants/triStateRange.enum';

const inputStyle = css({
  display: 'none',
});

const FullCheckmarkStyles = {
  left: '50%',
  top: '45%',
  width: '30%',
  height: '55%',
  transition: 'opacity .2s',
  borderWidth: '0 2px 2px 0',
  transform: 'translate(-50%, -50%) rotate(45deg)',
} as const;

const PartialCheckmarkStyles = {
  left: '25%',
  top: 'calc(50% - 1px)',
  width: '50%',
  height: 0,
  borderWidth: '1px 0',
} as const;

const Checkmark = styled.span<Readonly<{ checkmark: TriStateRange }>>(({ theme, checkmark }) => {
  const isChecked = checkmark !== TriStateRange.None;
  const backgroundColor = isChecked ? theme.checkboxColors.checkedFill : theme.checkboxColors.uncheckedFill;

  const checkmarkStyles = checkmark === TriStateRange.Full ? FullCheckmarkStyles
    : checkmark === TriStateRange.Partial ? PartialCheckmarkStyles
      : {};

  return {
    position: 'relative',
    height: '16px',
    width: '16px',
    display: 'block',
    boxSizing: 'border-box',
    backgroundColor,
    border: `solid ${theme.checkboxColors.uncheckedBorderTertiary}`,
    borderWidth: isChecked ? 0 : 1,
    borderRadius: '4px',
    transition: 'background .2s',
    flexShrink: 0,
    '&:after': {
      content: '""',
      position: 'absolute',
      boxSizing: 'border-box',
      opacity: isChecked ? 1 : 0,
      border: 'solid white',
      ...checkmarkStyles,
    },
  };
});

const labelStyle = ({ checkmark, isDisabled }: Readonly<{
  checkmark: TriStateRange;
  isDisabled?: boolean;
}>) => (theme: Theme) => {
  let checkmarkBackground = undefined;

  if (!isDisabled) {
    checkmarkBackground = checkmark !== TriStateRange.None
      ? theme.checkboxColors.checkedFill : theme.checkboxColors.checkedFillHover;
  }

  return css({
    position: 'relative',
    cursor: 'default',
    display: 'flex',
    fontSize: '13px',
    lineHeight: '21px',
    userSelect: 'none',
    alignItems: 'center',
    opacity: isDisabled ? theme.opacity.disabled : undefined,
    '&:hover': {
      [`${Checkmark}`]: {
        backgroundColor: checkmarkBackground,
      },
    },
  });
};

const textStyles = css({
  marginLeft: 15,
});

export type TriStateCheckboxComponentProps = Readonly<{
  id?: string;
  checkmark: TriStateRange;
  text?: ReactNode;
  className?: string;
  checkmarkStyle?: Interpolation<Theme>;
  textStyle?: Interpolation<Theme>;
  name?: string;
  isDisabled?: boolean;

  checkedSetter: ChangeEventHandler<HTMLInputElement>;
}>;

export const TriStateCheckboxComponent = forwardRef<HTMLLabelElement, TriStateCheckboxComponentProps>(({
  checkedSetter,
  checkmark,
  checkmarkStyle,
  className,
  id,
  isDisabled,
  name,
  text,
  textStyle,
  ...restProps
}, ref) => {
  return (
    <label
      {...restProps}
      ref={ref}
      className={className}
      css={labelStyle({ checkmark, isDisabled })}
    >
      <input
        id={id}
        type="checkbox"
        checked={checkmark !== TriStateRange.None}
        onChange={checkedSetter}
        css={inputStyle}
        disabled={isDisabled}
        name={name}
      />

      <Checkmark
        checkmark={checkmark}
        css={checkmarkStyle}
      />

      {text &&
        <span css={[textStyles, textStyle]}>{text}</span>
      }
    </label>
  );
});

TriStateCheckboxComponent.displayName = 'TriStateCheckboxComponent';
